import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { fetchEmailAccountHealth } from "../hooks/fetchEmailAccountHealth";
import { hasAnyEmailAccess, hasFullAccess } from "../utils/userAccess";

const EmailHealthContext = createContext({
    healthByEmail: {},
    loading: false,
    checkedAt: null,
    blockedCount: 0,
    warningCount: 0,
    issueCount: 0,
    refresh: () => {},
    getHealthForEmail: () => null,
    hasCountryIssue: () => false,
    countryHasBlocked: () => false,
});

function normalizeEmailKey(email) {
    return String(email || "").trim().toLowerCase();
}

function buildHealthMap(accounts) {
    const map = {};
    for (const item of accounts || []) {
        const key = normalizeEmailKey(item.email);
        if (key) map[key] = item;
    }
    return map;
}

export function EmailHealthProvider({ children }) {
    const [healthByEmail, setHealthByEmail] = useState({});
    const [loading, setLoading] = useState(false);
    const [checkedAt, setCheckedAt] = useState(null);
    const prevStatusRef = useRef({});
    const toastShownRef = useRef(new Set());

    const refresh = useCallback(async ({ showToastOnBlock = true } = {}) => {
        if (!Cookies.get("username")) return;
        if (!hasFullAccess() && !hasAnyEmailAccess()) return;

        setLoading(true);
        try {
            const { accounts, checkedAt: at } = await fetchEmailAccountHealth();
            const map = buildHealthMap(accounts);
            setHealthByEmail(map);
            setCheckedAt(at || new Date().toISOString());

            if (showToastOnBlock) {
                for (const item of accounts) {
                    const key = normalizeEmailKey(item.email);
                    const prev = prevStatusRef.current[key];
                    const isBlocked = item.status === "blocked";
                    const shouldToast =
                        isBlocked &&
                        (prev !== "blocked" || !toastShownRef.current.has(key));

                    if (shouldToast) {
                        const msg =
                            item.alerts?.find((a) => a.level === "error")?.message ||
                            `${item.email} par mail issue hai`;
                        toast.warn(`Email alert: ${msg}`, { autoClose: 9000 });
                        toastShownRef.current.add(key);
                    }
                    prevStatusRef.current[key] = item.status;
                }
            } else {
                for (const item of accounts) {
                    prevStatusRef.current[normalizeEmailKey(item.email)] = item.status;
                }
            }
        } catch {
            /* inbox still works without health */
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!Cookies.get("username")) return undefined;
        if (!hasFullAccess() && !hasAnyEmailAccess()) return undefined;

        refresh({ showToastOnBlock: true });
        const timer = setInterval(() => refresh({ showToastOnBlock: true }), 3 * 60 * 1000);
        return () => clearInterval(timer);
    }, [refresh]);

    const getHealthForEmail = useCallback(
        (email) => healthByEmail[normalizeEmailKey(email)] || null,
        [healthByEmail]
    );

    const hasCountryIssue = useCallback(
        (country) => {
            if (!country) return false;
            return Object.values(healthByEmail).some(
                (h) => h.country === country && h.status !== "ok"
            );
        },
        [healthByEmail]
    );

    const countryHasBlocked = useCallback(
        (country) => {
            if (!country) return false;
            return Object.values(healthByEmail).some(
                (h) => h.country === country && h.status === "blocked"
            );
        },
        [healthByEmail]
    );

    const blockedCount = useMemo(
        () => Object.values(healthByEmail).filter((h) => h.status === "blocked").length,
        [healthByEmail]
    );
    const warningCount = useMemo(
        () => Object.values(healthByEmail).filter((h) => h.status === "warning").length,
        [healthByEmail]
    );

    const value = useMemo(
        () => ({
            healthByEmail,
            loading,
            checkedAt,
            blockedCount,
            warningCount,
            issueCount: blockedCount + warningCount,
            refresh,
            getHealthForEmail,
            hasCountryIssue,
            countryHasBlocked,
        }),
        [
            healthByEmail,
            loading,
            checkedAt,
            blockedCount,
            warningCount,
            refresh,
            getHealthForEmail,
            hasCountryIssue,
            countryHasBlocked,
        ]
    );

    return <EmailHealthContext.Provider value={value}>{children}</EmailHealthContext.Provider>;
}

export function useEmailHealth() {
    return useContext(EmailHealthContext);
}

export { normalizeEmailKey as normalizeHealthEmailKey };
